/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Determines authentication check action based on state.
 * 
 * @param {boolean} shouldCheck - Whether check should be performed
 * @param {boolean} isLoading - Whether auth is loading
 * @returns {'check' | 'loading' | 'stable'} Action to take
 */
export function determineAuthAction(shouldCheck: boolean, isLoading: boolean): 'check' | 'loading' | 'stable' {
  if (shouldCheck && !isLoading) {
    return 'check';
  }
  if (isLoading) {
    return 'loading';
  }
  return 'stable';
}

/**
 * Handles authentication loading state.
 * 
 * @param {Function} setIsCheckingAuth - Setter for checking state
 * @returns {void}
 */
export function handleAuthLoading(setIsCheckingAuth: (value: boolean) => void): void {
  setIsCheckingAuth(true);
}

/**
 * Handles stable authentication state update.
 * 
 * @param {boolean} hasCheckedAuth - Whether auth has been checked
 * @param {boolean} authIsAuthenticated - Current auth state
 * @param {Function} setIsAuthenticated - Setter for authenticated state
 * @param {Function} setIsCheckingAuth - Setter for checking state
 * @returns {void}
 */
export function handleStableAuthState(
  hasCheckedAuth: boolean,
  authIsAuthenticated: boolean,
  setIsAuthenticated: (value: boolean) => void,
  setIsCheckingAuth: (value: boolean) => void
): void {
  if (hasCheckedAuth) {
    setIsAuthenticated(authIsAuthenticated);
    setIsCheckingAuth(false);
  }
}
